import { useEffect, useRef, useState } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { TableCell, TableRow } from 'src/components/ui/table';
import { clsx } from 'clsx';
import {
  WorkflowsChevronIcon,
  WorkflowsDragIndicatorIcon,
  WorkflowsEditIcon,
} from './WorkflowsRowIcons';
import { Switch } from 'src/components/ui/switch';
import { EditWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/EditWorkflowPopup/EditWorkflowPopup';
import { DeleteWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/DeleteWorkflowPopup/DeleteWorkflowPopup';
import { RowActionDescription } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/RowActionDescription/RowActionDescription';
import { API_VERSION } from 'src/constants/api-routes';
import { RowActionDescriptionV2 } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/RowActionDescriptionV2/RowActionDescriptionV2';
import { useConfigurationContext } from 'src/provider/ConfigurationProvider';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import { getApiVersion } from 'src/utils/apiVersion';

// What a dragged row carries; `index` is updated as the row moves over others.
interface DragItem {
  workflow: Workflow;
  index: number;
}

const WORKFLOWS_ROW_TYPE = 'WORKFLOWS_ROW';
const WorkflowDescription = ({ workflow, isV1 }: { workflow: Workflow; isV1: boolean }) =>
  isV1 ? (
    <>
      <RowActionDescription first={true} isAction={false} workflow={workflow} />
      {Array.isArray(workflow.action) ? (
        workflow.action.map((action, idx, array) => (
          <RowActionDescription
            first={false}
            last={idx === array.length - 1}
            key={`${action.type}-${idx}`}
            isAction={true}
            workflow={workflow}
            action={action}
          />
        ))
      ) : (
        <RowActionDescription
          last={true}
          isAction={true}
          workflow={workflow}
          action={workflow.action}
        />
      )}
    </>
  ) : (
    <RowActionDescriptionV2 workflow={workflow} />
  );

// Only move once the pointer passes the middle of the hovered row, so rows do not flicker.
const hasCrossedRowMiddle = (
  dragIndex: number,
  hoverIndex: number,
  hoverRect: DOMRect,
  hoverClientY: number,
) => {
  const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;

  if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return false;
  if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return false;
  return true;
};

export const WorkflowsDragableRow = ({
  index = 0,
  row = {} as Workflow,
  moveRow = (() => {}) as (dragIndex: number, hoverIndex: number) => void,
  readonly = true,
  isCustom = true,
  compact = true,
  onWorkflowChange = (() => {}) as (workflow: Workflow, index: number) => void,
  onWorkflowDelete = (() => {}) as (index: number) => void,
  rowClassName = '',
}: {
  index?: number;
  row?: Workflow;
  moveRow?: (dragIndex: number, hoverIndex: number) => void;
  readonly?: boolean;
  isCustom?: boolean;
  compact?: boolean;
  onWorkflowChange?: (workflow: Workflow, index: number) => void;
  onWorkflowDelete?: (index: number) => void;
  rowClassName?: string;
}) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [workflow, setWorkflow] = useState<Workflow>({ ...row });
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { setIsEditWorkflowMode, setSelectedWorkflow } = useConfigurationContext();
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;

  useEffect(() => {
    setWorkflow({ ...row });
  }, [readonly, row]);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: ReturnType<DropTargetMonitor['getHandlerId']> }
  >({
    accept: WORKFLOWS_ROW_TYPE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current || readonly) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverClientY = monitor.getClientOffset().y - hoverBoundingRect.top;

      if (!hasCrossedRowMiddle(dragIndex, hoverIndex, hoverBoundingRect, hoverClientY)) {
        return;
      }
      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  const [collected, drag] = useDrag({
    type: WORKFLOWS_ROW_TYPE,
    item: () => {
      return { workflow, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = collected.isDragging ? 0 : 1;
  drag(drop(ref));

  const onChange = (newWorkflow: Workflow) => {
    onWorkflowChange(newWorkflow, index);
  };

  const onDelete = () => {
    onWorkflowDelete(index);
  };

  return (
    <TableRow
      ref={ref}
      className={clsx(
        !readonly && 'cursor-grab',
        readonly && 'cursor-pointer',
        isHovered && 'row-hover',
        rowClassName,
      )}
      style={{ opacity }}
      data-handler-id={handlerId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell
        colSpan={2}
        padding={'p-0'}
        className={clsx(
          '!rounded-none !border-l-0 !border-r-0 !border-t-0 transition-colors duration-300 border-b border-b-light align-top',
          !readonly && isHovered && '!border-b-violet',
        )}
      >
        <div className={clsx('flex w-full flex-col', expanded && 'pb-2')}>
          <div
            role="none"
            className={clsx(
              'flex w-full flex-row items-center justify-between gap-3 px-3 py-3 transition-colors duration-300',
              !expanded && 'pb-3',
              !readonly && isHovered && 'bg-light',
            )}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <button
              type={'button'}
              className={'flex min-w-0 flex-row items-center gap-2'}
              title={workflow.name}
            >
              <span
                className={clsx(
                  'inline-block overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-ebony',
                  compact ? 'max-w-[350px]' : 'max-w-[200px]',
                )}
              >
                {workflow.name}
              </span>
              <WorkflowsChevronIcon expanded={expanded} />
            </button>
            <div className={'flex shrink-0 flex-row items-center gap-5'}>
              {!readonly && (
                <div
                  className={clsx(
                    'transition-opacity duration-300 opacity-0',
                    isHovered && 'opacity-100',
                  )}
                >
                  <WorkflowsDragIndicatorIcon />
                </div>
              )}
              {!readonly && isCustom && <DeleteWorkflowPopup row={row} onSubmit={onDelete} />}
              {!readonly && isV1 ? (
                <EditWorkflowPopup isCustom={isCustom} row={row} onSubmit={onChange} />
              ) : (
                !readonly && (
                  <WorkflowsEditIcon
                    onClick={() => {
                      setSelectedWorkflow({ ...workflow, index });
                      setIsEditWorkflowMode(true);
                    }}
                  />
                )
              )}
              <Switch
                onClick={(e) => e.stopPropagation()}
                defaultChecked={workflow.enabled}
                checked={workflow.enabled}
                onCheckedChange={(enabled) => onChange({ ...workflow, enabled })}
                disabled={readonly || !isCustom}
              />
            </div>
          </div>
          {expanded && (
            <div className={'flex w-full flex-col items-start gap-3 px-3 py-3 bg-light'}>
              <WorkflowDescription workflow={workflow} isV1={isV1} />
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
